import { DEAL_TYPES, editProblems, fieldDefault, t, visibleEditFields, type DealType } from '@bms2/shared';
import { PAGES_PATH, post } from './api';
import { PARTS_DEALS_PATH, partsDealBody, type PartsDealDraftState } from './PartsDealFields';
import { SERVICE_REQUESTS_PATH, serviceRequestBody, type ServiceRequestDraft } from './ServiceRequestFields';

/**
 * 🔒 The three New Deal forms' one save (piece 18, *New Deal by type*;
 * decisions 2290, 2510): the Parts form posts to its own route, the Service
 * form to the request route, and the Project form through the Deal's own
 * create. The New Deal page and the window a picker's + Add opens both call
 * this, so the two cannot drift into two saves of one form.
 */
export interface DealFormCreated {
  id: string;
  label: string;
  duplicates?: unknown[];
  /** Why an emergency's confirmation text did not go after the Deal and job were saved (T1781). */
  textNotSent?: string;
}

export type DealFormSaved = { ok: true; data: DealFormCreated } | { ok: false; message: string };

/** 🔒 The Deal's own fields the Parts and Service forms keep; neither asks a name, route, value or stage (decision 2290). */
export const SHORT_FORM_FIELDS: readonly string[] = ['companyId', 'dealType'];

/** The Project form's fields on the New Deal page, where the tiles above it carry the Type (decision 2510). */
export const PROJECT_PAGE_FIELDS: readonly string[] = ['name', 'companyId', 'origin', 'dealValue', 'expectedQuoteDate', 'expectedCloseDate', 'expectedFinishDate'];

/** A held value read as one of the three types, or null where it is none of them. */
export function dealTypeOf(value: unknown): DealType | null {
  return typeof value === 'string' && (DEAL_TYPES as readonly string[]).includes(value) ? (value as DealType) : null;
}

export interface DealFormInput {
  values: Record<string, unknown>;
  parts: PartsDealDraftState;
  request: ServiceRequestDraft;
  /** The price the Parts form showed for an Item, so a price left as shown is not sent (decision 0440). */
  listPriceOf: (itemId: string) => number | null;
  zone: string;
  apiBaseUrl: string;
}

/** Saves the form the held Type draws, or names the problem before anything is sent. */
export async function saveDealForm({ values, parts, request, listPriceOf, zone, apiBaseUrl }: DealFormInput): Promise<DealFormSaved> {
  const type = dealTypeOf(values.dealType);
  if (type === 'Parts') {
    const shaped = partsDealBody(values, parts, listPriceOf);
    if (!shaped.ok) return { ok: false, message: shaped.problem };
    return post<DealFormCreated>(PARTS_DEALS_PATH, shaped.body, apiBaseUrl);
  }
  if (type === 'Service') {
    const shaped = serviceRequestBody(values, request, zone);
    if (!shaped.ok) return { ok: false, message: shaped.problem };
    return post<DealFormCreated>(SERVICE_REQUESTS_PATH, shaped.body, apiBaseUrl);
  }
  // The fields the form drew, a choice left untouched sent as its default and a hidden field left off (decision 0353).
  const drawn = visibleEditFields('Deal', 'create', (key) => values[key]);
  const body = Object.fromEntries(drawn.map((field) => [field.key, values[field.key] ?? fieldDefault(field, (key) => values[key]) ?? null]));
  const missing = editProblems('Deal', body);
  if (missing.length > 0) return { ok: false, message: `${t('edit.required')} ${missing.join(', ')}` };
  // 🔒 The Owner the Project form picked rides beside the fields (decision 10701); absent, the person saving owns the Deal.
  const owner = typeof values.ownerEmploymentId === 'string' && values.ownerEmploymentId ? { ownerEmploymentId: values.ownerEmploymentId } : {};
  return post<DealFormCreated>(`${PAGES_PATH}/records/Deal`, { ...body, ...owner }, apiBaseUrl);
}
